import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { FetchAPI, FilterAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnClick, OnDynamicContentRender, OnMount, OnSelectItem } from '@philips-emr/tws-core/lib/client';
import { MountEvent, RenderEvent, SelectItemEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';

import { FragmentNames } from '../codegen';
import { CnesSearchAliases as Aliases } from '../codegen/constants/aliases/cnes-search-aliases';
import { Route } from '../route';

import BaseController from './BaseController';

enum StatusCnesSearch {
  ONGOING_CONSULTATION = 0,
  UPDATED = 1,
  OUTDATED = 2
}

interface CnesSearch {
  nrSeqCnesSearch: number;
  dtUpdate: string;
  ieStatus: StatusCnesSearch;
  dsStatus: string;
  ongoingSearch: string;
  cdCnes: string;
}

@Controller({ fragment: FragmentNames.CNES_SEARCH })
class CnesSearchController extends BaseController {

  @Inject(Aliases.FETCH_CNES_SEARCH)
  private fetchCnesSearch: FetchAPI;

  @OnMount([Aliases.FETCH_CNES_SEARCH, Aliases.FETCH_MESSAGE])
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    api.setParameters({ nrSeqProvider });
    api.forceFetch();
  }

  @OnDynamicContentRender(Aliases.CARD_CNES_SEARCH)
  onRenderStatus(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as unknown as CnesSearch;
    const stampVisit = getStampStatusCnes(ieStatus, dsStatus);

    if (stampVisit) {
      render((): ReactElement => stampVisit);
    }
  }

  @OnClick(Aliases.ACTION_NEW_SEARCH)
  onClickNewSearch(): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    HttpService.post<Record<string, Value>>(Endpoint.POST.NEW_SEARCH_CNES, { nrSeqProvider }, {}).then((resp) => {
      this.fetchCnesSearch.forceFetch();
      ToastService.open(resp);
    }).catch(error => {
      ToastService.open(error);
    });
  }

  @OnSelectItem(Aliases.DECK_CNES_SEARCH)
  onSelectCard(event: SelectItemEvent<CnesSearch>): void {
    const nrSeqCnesSearch = event.item.nrSeqCnesSearch;
    this.historyService.push(Route.CNES_SEARCH_DETAIL, { nrSeqCnesSearch } );
  }

  @OnMount(Aliases.FILTER_CNES)
  onFilterMount(event: MountEvent): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const api = event.target as FilterAPI;
    api.setData({ nrSeqProvider });
  }

}

function getStampStatusCnes(ieStatus: StatusCnesSearch, label: string): ReactElement {
  let stampProps = null;
  switch (ieStatus) {
    case StatusCnesSearch.ONGOING_CONSULTATION:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
      break;
    case StatusCnesSearch.UPDATED:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case StatusCnesSearch.OUTDATED:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label };
      break;
  }
  return <Stamp {...stampProps} />;
}

export {
  CnesSearch,
  CnesSearchController,
  getStampStatusCnes,
  StatusCnesSearch,
};
